// ============================================================================
// Dashboard.js — Repeaked (v3)
//
// Changes from v2:
//   • Quick stats SWAPPED with drop widget (stats now in right column,
//     drop widget removed from desktop)
//   • Party view added to centre column below FIND MATCH CTA
//   • Top-right of header now shows live status panel (no more empty space)
//   • Friends widget falls back to offline friends if none are online
//   • Offline friends shown in the widget when nobody's online
// ============================================================================

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useQueue, MODE_5V5, MODE_WINGMAN } from '../context/QueueContext';
import {
  getPlayerRank,
  getEloHistory,
  getMatchHistory,
  getFriends,
  getFriendRequests,
  acceptFriend,
  addFriend,
  searchUsers,
  sendPartyInvite,
  readNotification, 
  joinParty,
  leaveParty,
  kickFromParty,
  promotePartyLeader,
  setPartyMode,
} from '../api/api';

// ---------------------------------------------------------------------------
// Brand tokens
// ---------------------------------------------------------------------------
const C = {
  red:          '#E8192C',
  redDim:       '#a4111d',
  redGlow:      'rgba(232, 25, 44, 0.45)',
  orange:       '#FF6B2B',
  green:        '#3FCB7E',
  bg:           '#0C0905',
  bgWarm:       '#100B07',
  bgWarmer:     '#15100B',
  text:         '#F0F2F5',
  textDim:      '#9A938B',
  textVeryDim:  '#5C554E',
  card:         '#111111',
  cardAlt:      '#16110D',
  border:       '#1e1e1e',
  borderBright: '#2A2520',
};

const F = {
  display: '"Bebas Neue", "Oswald", Impact, sans-serif',
  body:    '"Rajdhani", "Inter", system-ui, -apple-system, sans-serif',
};

// ---------------------------------------------------------------------------
// Rank system — ranges per project doc Section 7
// ---------------------------------------------------------------------------
const RANKS = [
  { name: 'Rust',                       min: 0,    max: 400,  file: 'rust.png' },
  { name: 'Rust Elite',                 min: 401,  max: 700,  file: 'rust-elite.png' },
  { name: 'Shadow Nova',                min: 701,  max: 1000, file: 'shadow-nova.png' },
  { name: 'Shadow Nova Master',         min: 1001, max: 1300, file: 'shadow-nova-master.png' },
  { name: 'Phantom Guardian',           min: 1301, max: 1550, file: 'phantom-guardian.png' },
  { name: 'Phantom Guardian Elite',     min: 1551, max: 1800, file: 'phantom-guardian-elite.png' },
  { name: 'Distinguished Phantom',      min: 1801, max: 2000, file: 'distinguished-phantom.png' },
  { name: 'Legendary Wraith',           min: 2001, max: 2200, file: 'legendary-wraith.png' },
  { name: 'Legendary Wraith Master',    min: 2201, max: 2500, file: 'legendary-wraith-master.png' },
  { name: 'Supreme Legacy',             min: 2501, max: 2750, file: 'supreme-legacy.png' },
  { name: 'Repeaked',                   min: 2751, max: 3000, file: 'repeaked.png' },
];

const eloToRank = (elo) => {
  if (elo == null || typeof elo !== 'number') return null;
  return RANKS.find(r => elo >= r.min && elo <= r.max)
    || (elo > 3000 ? RANKS[RANKS.length - 1] : RANKS[0]);
};

const rankSlug = (rankName) => String(rankName || 'unranked').toLowerCase().replace(/ /g, '-');
const rankImageCandidates = (rankName) => {
  const slug = rankSlug(rankName);
  if (slug === 'unranked') {
    return [
      '/ranks/unranked.png',
      '/ranks/Unranked.png',
      '/ranks/unranked.webp',
      '/ranks/unranked.svg',
      '/assets/ranks/unranked.png',
    ];
  }
  return [`/ranks/${slug}.png`, `/ranks/${slug}.webp`, `/assets/ranks/${slug}.png`];
};

const rankList = (payload) => {
  if (Array.isArray(payload?.ranks)) return payload.ranks;
  if (payload?.primary) return [payload.primary];
  return payload ? [payload] : [];
};

const normalizeRankMode = (mode) => {
  const value = String(mode || MODE_5V5).toLowerCase();
  if (value === 'wingman' || value === '2v2') return 'wingman';
  return 'fivevfive';
};

const rankForMode = (payload, mode) => {
  const ranks = rankList(payload);
  const normalizedMode = normalizeRankMode(mode);
  return ranks.find(r => normalizeRankMode(r?.mode) === normalizedMode) || ranks.find(r => r?.mode === 'fivevfive') || ranks[0] || null;
};

const publicRankName = (payload, mode) => {
  const rank = rankForMode(payload, mode);
  if (!rank || rank.is_ranked !== true || rank.elo == null) return 'Unranked';
  return rank.rank || rank.rank_name || eloToRank(rank.elo)?.name || 'Unranked';
};

const publicRankElo = (payload, mode) => {
  const rank = rankForMode(payload, mode);
  return rank?.is_ranked === true && rank.elo != null ? rank.elo : null;
};

const rankPlacementLabel = (payload, mode) => {
  const rank = rankForMode(payload, mode);
  if (!rank || rank.is_ranked) return null;
  const required = rank.placement_games_required || rank.placement_matches_required || (normalizeRankMode(mode) === 'wingman' ? 3 : 5);
  const played = rank.placement_games_played || rank.placement_matches_played || 0;
  return `${played}/${required} placements`;
};

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------
const useIsMobile = (bp = 1180) => {
  const [m, setM] = useState(typeof window !== 'undefined' ? window.innerWidth < bp : false);
  useEffect(() => {
    const h = () => setM(window.innerWidth < bp);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, [bp]);
  return m;
};

const useHover = () => {
  const [h, setH] = useState(false);
  return [h, { onMouseEnter: () => setH(true), onMouseLeave: () => setH(false) }];
};

// ---------------------------------------------------------------------------
// Format helpers
// ---------------------------------------------------------------------------
const fmtTime = (secs) => {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const fmtNum = (n, decimals = 0) => {
  if (n === null || n === undefined || Number.isNaN(n) || typeof n !== 'number') return '—';
  return decimals > 0 ? n.toFixed(decimals) : String(Math.round(n));
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d) / 1000;
    if (diff < 60)        return 'just now';
    if (diff < 3600)      return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400)     return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleDateString();
  } catch { return '—'; }
};

// ===========================================================================
// ATOMS
// ===========================================================================
const Diamond = ({ size = 16, color = C.red, dotColor = C.orange, withDot = false, hollow = true, style }) => (
  <span aria-hidden="true" style={{
    display: 'inline-block', width: size, height: size,
    border: `${Math.max(1.5, size / 9)}px solid ${color}`,
    background: hollow ? 'transparent' : color,
    transform: 'rotate(45deg)', position: 'relative',
    boxSizing: 'border-box', flexShrink: 0, ...style,
  }}>
    {withDot && (
      <span style={{
        position: 'absolute', top: '50%', left: '50%',
        width: size * 0.32, height: size * 0.32, background: dotColor,
        transform: 'translate(-50%, -50%)',
      }} />
    )}
  </span>
);

const Eyebrow = ({ children, style }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: 10,
    fontFamily: F.body, fontSize: 10, fontWeight: 700,
    letterSpacing: 2.5, textTransform: 'uppercase', color: C.textDim,
    ...style,
  }}>
    <Diamond size={6} color={C.red} hollow={false} />
    <span>{children}</span>
  </div>
);

const Icon = ({ d, size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="1.5" strokeLinecap="square" strokeLinejoin="miter" aria-hidden="true">
    {d}
  </svg>
);
const IconX      = (p) => <Icon {...p} d={<><path d="M6 6l12 12M18 6l-12 12"/></>} />;
const IconSearch = (p) => <Icon {...p} d={<><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.5-4.5"/></>} />;

const RankImage = ({ rankName, size, fallbackSize }) => {
  const sources = rankImageCandidates(rankName);
  const [sourceIndex, setSourceIndex] = useState(0);
  const src = sources[sourceIndex];

  useEffect(() => {
    setSourceIndex(0);
  }, [rankName]);

  if (!src) {
    return <Diamond size={fallbackSize || size * 0.7} color={C.red} withDot dotColor={C.orange} />;
  }

  return (
    <img
      src={src}
      alt={rankName}
      onError={() => setSourceIndex((current) => current + 1)}
      style={{ width: size, height: size, objectFit: 'contain' }}
    />
  );
};

// Small rank badge — used in player cards
const RankBadge = ({ elo, rankInfo, mode, size = 36 }) => {
  const rankName = rankInfo ? publicRankName(rankInfo, mode) : null;
  const rank = rankInfo
    ? (rankName && rankName !== 'Unranked' ? RANKS.find(r => r.name.toLowerCase() === rankName.toLowerCase()) : null)
    : eloToRank(elo);
  const imageRankName = rank?.name || (rankInfo ? 'Unranked' : null);
  return (
    <div style={{
      width: size, height: size, flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      {imageRankName
        ? <RankImage rankName={imageRankName} size={size} />
        : <Diamond size={size * 0.7} color={C.red} withDot dotColor={C.orange} />
      }
    </div>
  );
};

// ===========================================================================
// LEFT COLUMN — Rank, Sparkline (no quick stats here anymore)
// ===========================================================================
const RankCard = ({ rank, eloDelta, mode }) => {
  const rankData = rankForMode(rank, mode);
  const elo      = publicRankElo(rank, mode);
  const rankName = publicRankName(rank, mode);
  const placement = rankPlacementLabel(rank, mode);
  const wins     = rankData?.wins ?? 0;
  const losses   = rankData?.losses ?? 0;

  return (
    <div style={{
      background: C.card,
      borderTop:    `1px solid ${C.border}`,
      borderRight:  `1px solid ${C.border}`,
      borderBottom: `1px solid ${C.border}`,
      borderLeft:   `2px solid ${C.red}`,
      padding: 22, display: 'flex', flexDirection: 'column', gap: 16,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <div style={{
          width: 64, height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: C.cardAlt,
          borderTop: `1px solid ${C.border}`, borderRight: `1px solid ${C.border}`,
          borderBottom: `1px solid ${C.border}`, borderLeft: `1px solid ${C.border}`,
        }}>
          <RankImage rankName={rankName} size={56} fallbackSize={32} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: F.body, fontSize: 10, fontWeight: 700, letterSpacing: 2.5, textTransform: 'uppercase', color: C.textDim }}>
            Current rank
          </div>
          <div style={{ fontFamily: F.display, fontSize: 22, letterSpacing: 1.5, color: C.text, lineHeight: 1.05, textTransform: 'uppercase', marginTop: 4 }}>
            {rankName}
          </div>
        </div>
      </div>

      <div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <span style={{ fontFamily: F.display, fontSize: 88, lineHeight: 0.85, letterSpacing: 1, color: C.text, fontVariantNumeric: 'tabular-nums' }}>
            {elo ?? '—'}
          </span>
          {eloDelta != null && (
            <span style={{ fontFamily: F.display, fontSize: 18, letterSpacing: 1.5, color: eloDelta >= 0 ? C.green : C.red, lineHeight: 1 }}>
              {eloDelta >= 0 ? '+' : ''}{eloDelta}
            </span>
          )}
        </div>
        <div style={{ fontFamily: F.body, fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: C.textDim, marginTop: 2 }}>
          {placement || 'Elo · Season'}
        </div>
      </div>

      <div style={{
        display: 'grid', gridTemplateColumns: '1fr 1fr',
        borderTop: `1px solid ${C.border}`,
        borderRight: 'none', borderBottom: 'none', borderLeft: 'none',
        paddingTop: 14,
      }}>
        <div>
          <div style={{ fontFamily: F.display, fontSize: 28, color: C.green, lineHeight: 1 }}>{wins}</div>
          <div style={{ fontFamily: F.body, fontSize: 10, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: C.textDim, marginTop: 4 }}>Wins</div>
        </div>
        <div>
          <div style={{ fontFamily: F.display, fontSize: 28, color: C.red, lineHeight: 1 }}>{losses}</div>
          <div style={{ fontFamily: F.body, fontSize: 10, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: C.textDim, marginTop: 4 }}>Losses</div>
        </div>
      </div>
    </div>
  );
};

const Sparkline = ({ points = [], width = 280, height = 64, color = C.orange }) => {
  if (!points || points.length < 2) {
    return (
      <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: F.body, fontSize: 11, color: C.textVeryDim, letterSpacing: 1.5, textTransform: 'uppercase', fontWeight: 600 }}>
        Not enough matches yet
      </div>
    );
  }
  const min = Math.min(...points), max = Math.max(...points), range = max - min || 1;
  const stepX = width / (points.length - 1);
  const coords = points.map((p, i) => [i * stepX, height - ((p - min) / range) * (height - 6) - 3]);
  const path   = coords.map(([x, y], i) => `${i === 0 ? 'M' : 'L'} ${x} ${y}`).join(' ');
  const area   = `${path} L ${width} ${height} L 0 ${height} Z`;
  return (
    <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
      <defs>
        <linearGradient id="sparkFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.25" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill="url(#sparkFill)" />
      <path d={path} fill="none" stroke={color} strokeWidth="1.5" />
      {coords.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r={i === coords.length - 1 ? 3 : 1.5} fill={i === coords.length - 1 ? C.red : color} />
      ))}
    </svg>
  );
};

const SparklineCard = ({ points }) => (
  <div style={{
    background: C.card,
    borderTop: `1px solid ${C.border}`, borderRight: `1px solid ${C.border}`,
    borderBottom: `1px solid ${C.border}`, borderLeft: `2px solid ${C.borderBright}`,
    padding: 18,
  }}>
    <Eyebrow style={{ marginBottom: 12 }}>Elo · last 10 matches</Eyebrow>
    <Sparkline points={points} />
  </div>
);

// ===========================================================================
// RIGHT COLUMN — Quick Stats (moved from left)
// ===========================================================================
const PanelStat = ({ value, label, accent = false }) => (
  <div style={{
    padding: '14px 16px',
    background: accent ? `linear-gradient(180deg, ${C.cardAlt}, ${C.card})` : C.card,
    borderTop:    `1px solid ${accent ? C.borderBright : C.border}`,
    borderRight:  `1px solid ${accent ? C.borderBright : C.border}`,
    borderBottom: `1px solid ${accent ? C.borderBright : C.border}`,
    borderLeft:   `2px solid ${accent ? C.red : C.borderBright}`,
    display: 'flex', flexDirection: 'column', gap: 3,
  }}>
    <div style={{ fontFamily: F.display, fontSize: 28, letterSpacing: 1, lineHeight: 1, color: C.text, fontVariantNumeric: 'tabular-nums' }}>
      {value}
    </div>
    <div style={{ fontFamily: F.body, fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: C.textDim, fontWeight: 700 }}>
      {label}
    </div>
  </div>
);

const QuickStatsCard = ({ stats }) => (
  <div style={{
    background: C.card,
    borderTop: `1px solid ${C.border}`, borderRight: `1px solid ${C.border}`,
    borderBottom: `1px solid ${C.border}`, borderLeft: `2px solid ${C.orange}`,
    padding: 16,
  }}>
    <Eyebrow style={{ marginBottom: 14 }}>Career stats</Eyebrow>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
      <PanelStat value={fmtNum(stats?.kd, 2)} label="Avg K/D" accent />
      <PanelStat value={stats?.winRate != null ? `${Math.round(stats.winRate)}%` : '—'} label="Win rate" />
      <PanelStat value={stats?.hs != null ? `${Math.round(stats.hs)}%` : '—'} label="HS %" />
      <PanelStat value={fmtNum(stats?.matches)} label="Matches" accent />
    </div>
  </div>
);

// ===========================================================================
// CENTRE COLUMN — Recent Matches, FIND MATCH CTA, Party View
// ===========================================================================
const ResultPill = ({ result }) => {
  const win = result === 'win' || result === 'W';
  const colour = win ? C.green : result ? C.red : C.borderBright;
  return (
    <span style={{
      display: 'inline-block', padding: '3px 10px',
      fontFamily: F.display, fontSize: 13, letterSpacing: 2, textTransform: 'uppercase', color: colour,
      borderTop: `1.5px solid ${colour}`, borderRight: `1.5px solid ${colour}`,
      borderBottom: `1.5px solid ${colour}`, borderLeft: `1.5px solid ${colour}`,
      background: colour === C.borderBright ? 'transparent' : `${colour}10`,
      lineHeight: 1,
    }}>
      {win ? 'Win' : result ? 'Loss' : '—'}
    </span>
  );
};

const MatchCard = ({ match, onClick }) => {
  const [hover, hp] = useHover();
  const result = match.result ||
    (match.team_score != null && match.opponent_score != null
      ? (match.team_score > match.opponent_score ? 'win' : 'loss') : null);

  const mapId   = match.map || match.map_name || '';
  const mapName = match.map_display_name ||
    (mapId.replace(/^de_/, '').charAt(0).toUpperCase() + mapId.replace(/^de_/, '').slice(1));
  const mapImg  = mapId.replace(/^de_/, '');

  const score = match.team_score != null && match.opponent_score != null
    ? `${match.team_score}–${match.opponent_score}` : '—';

  const k  = match.kills       ?? match.k  ?? null;
  const d  = match.deaths      ?? match.d  ?? null;
  const a  = match.assists     ?? match.a  ?? null;
  const hs = match.hs_percent  ?? match.headshot_percent ?? null;
  const kda = [k, d, a].every(x => x != null) ? `${k} / ${d} / ${a}` : '—';

  return (
    <button {...hp} onClick={() => onClick?.(match)} style={{
      display: 'flex', alignItems: 'center', gap: 14,
      width: '100%', padding: '12px 14px',
      background: hover ? C.cardAlt : C.card,
      borderTop:    `1px solid ${hover ? C.borderBright : C.border}`,
      borderRight:  `1px solid ${hover ? C.borderBright : C.border}`,
      borderBottom: `1px solid ${hover ? C.borderBright : C.border}`,
      borderLeft:   `2px solid ${result === 'win' || result === 'W' ? C.green : result === 'loss' || result === 'L' ? C.red : C.borderBright}`,
      cursor: 'pointer', textAlign: 'left',
      transition: 'background 160ms ease, border-color 160ms ease',
    }}>
      <div style={{
        width: 64, height: 48, flexShrink: 0, background: C.bgWarmer,
        borderTop: `1px solid ${C.border}`, borderRight: `1px solid ${C.border}`,
        borderBottom: `1px solid ${C.border}`, borderLeft: `1px solid ${C.border}`,
        backgroundImage: mapImg ? `url(/assets/maps/${mapImg}.jpg)` : 'none',
        backgroundSize: 'cover', backgroundPosition: 'center', position: 'relative',
      }}>
        <span style={{ position: 'absolute', inset: 0, background: 'linear-gradient(0deg,rgba(12,9,5,0.6),rgba(12,9,5,0.1))' }} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, flex: 1 }}>
        <span style={{ fontFamily: F.display, fontSize: 18, letterSpacing: 1.5, color: C.text, textTransform: 'uppercase', lineHeight: 1 }}>
          {mapName || 'Unknown'}
        </span>
        <span style={{ fontFamily: F.body, fontSize: 11, fontWeight: 600, letterSpacing: 1, color: C.textDim, textTransform: 'uppercase' }}>
          {fmtDate(match.finished_at || match.created_at || match.date)}
        </span>
      </div>
      <ResultPill result={result || ''} />
      <div style={{ fontFamily: F.display, fontSize: 22, color: C.text, letterSpacing: 1.5, lineHeight: 1, fontVariantNumeric: 'tabular-nums', width: 64, textAlign: 'right' }}>
        {score}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2, width: 100, flexShrink: 0 }}>
        <span style={{ fontFamily: F.display, fontSize: 15, letterSpacing: 1, color: C.text, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
          {kda}
        </span>
        <span style={{ fontFamily: F.body, fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: C.textDim, textTransform: 'uppercase' }}>
          {hs != null ? `${Math.round(hs)}% HS` : 'K / D / A'}
        </span>
      </div>
    </button>
  );
};

const RecentMatches = ({ matches, onOpenMatch }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
    <Eyebrow>Recent matches</Eyebrow>
    {matches.length === 0 ? (
      <div style={{
        padding: 22, background: C.card,
        borderTop: `1px solid ${C.border}`, borderRight: `1px solid ${C.border}`,
        borderBottom: `1px solid ${C.border}`, borderLeft: `2px solid ${C.borderBright}`,
        fontFamily: F.body, fontSize: 13, color: C.textDim, fontWeight: 500, lineHeight: 1.5,
      }}>
        No matches yet. Hit FIND MATCH below — queue's been waiting.
      </div>
    ) : (
      matches.map(m => (
        <MatchCard key={m.id || m.match_id || Math.random()} match={m} onClick={onOpenMatch} />
      ))
    )}
  </div>
);

const FindMatchCTA = () => {
  const { queueActive, queueMode, queueElapsed, openQueueOverlay, cancelQueue, queueLoading, activeMapPool, activeMapList } = useQueue();
  const [hover, hp] = useHover();

  if (queueActive) {
    const modeLabel = queueMode === MODE_WINGMAN ? 'Wingman' : '5v5 Competitive';
    return (
      <div style={{
        background: C.card, padding: 22, position: 'relative', overflow: 'hidden',
        borderTop: `1px solid ${C.red}`, borderRight: `1px solid ${C.red}`,
        borderBottom: `1px solid ${C.red}`, borderLeft: `2px solid ${C.red}`,
        display: 'flex', flexDirection: 'column', gap: 14,
      }}>
        <Diamond size={260} color={C.red} style={{ position: 'absolute', right: -110, bottom: -110, opacity: 0.05, pointerEvents: 'none' }} />
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: C.red, animation: 'repeakedPulse 1.4s ease-in-out infinite' }} />
          <span style={{ fontFamily: F.body, fontSize: 11, fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase', color: C.red }}>In queue</span>
        </div>
        <div style={{ position: 'relative', fontFamily: F.display, fontSize: 48, letterSpacing: 1.5, color: C.text, lineHeight: 0.95, textTransform: 'uppercase' }}>
          {modeLabel}<span style={{ color: C.red }}>.</span>
        </div>
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 18, fontFamily: F.body, fontSize: 12, fontWeight: 600, letterSpacing: 1.5, color: C.textDim, textTransform: 'uppercase' }}>
          <span>
            <span style={{ fontFamily: F.display, fontSize: 22, color: C.text, letterSpacing: 1.5, marginRight: 8, fontVariantNumeric: 'tabular-nums' }}>
              {fmtTime(queueElapsed)}
            </span>
            elapsed
          </span>
          <span style={{ color: C.textVeryDim }}>·</span>
          <span>{activeMapPool?.length || 0} / {activeMapList?.length || 0} maps</span>
        </div>
        <div style={{ position: 'relative', display: 'flex', gap: 10, marginTop: 4 }}>
          <button onClick={openQueueOverlay} style={{ background: 'transparent', color: C.text, borderTop: `1.5px solid ${C.borderBright}`, borderRight: `1.5px solid ${C.borderBright}`, borderBottom: `1.5px solid ${C.borderBright}`, borderLeft: `1.5px solid ${C.borderBright}`, padding: '10px 18px', cursor: 'pointer', fontFamily: F.display, fontSize: 13, letterSpacing: 2, textTransform: 'uppercase' }}>
            View queue
          </button>
          <button onClick={cancelQueue} disabled={queueLoading} style={{ background: 'transparent', color: C.red, borderTop: `1.5px solid ${C.red}`, borderRight: `1.5px solid ${C.red}`, borderBottom: `1.5px solid ${C.red}`, borderLeft: `1.5px solid ${C.red}`, padding: '10px 18px', cursor: queueLoading ? 'wait' : 'pointer', fontFamily: F.display, fontSize: 13, letterSpacing: 2, textTransform: 'uppercase' }}>
            Cancel queue
          </button>
        </div>
      </div>
    );
  }

  return (
    <button {...hp} onClick={openQueueOverlay} style={{
      position: 'relative', width: '100%', padding: '28px 28px',
      background: hover ? `linear-gradient(135deg, ${C.red} 0%, ${C.redDim} 100%)` : C.red,
      color: C.text,
      borderTop: 'none', borderRight: 'none', borderBottom: 'none', borderLeft: 'none',
      cursor: 'pointer', textAlign: 'left',
      boxShadow: hover ? `0 14px 30px -10px ${C.redGlow}, 0 0 0 1px ${C.orange}40` : `0 8px 20px -10px ${C.redGlow}`,
      transform: hover ? 'translateY(-2px)' : 'translateY(0)',
      transition: 'transform 200ms ease, box-shadow 200ms ease, background 200ms ease',
      overflow: 'hidden',
    }}>
      <Diamond size={220} color={C.text} style={{ position: 'absolute', right: -90, top: -90, opacity: 0.08, pointerEvents: 'none' }} />
      <div style={{ fontFamily: F.body, fontSize: 11, fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase', color: 'rgba(255,255,255,0.7)', marginBottom: 8 }}>
        ◆ The lobby is open
      </div>
      <div style={{ fontFamily: F.display, fontSize: 56, lineHeight: 0.9, letterSpacing: 1.5, color: C.text, textTransform: 'uppercase' }}>
        Find match<span style={{ color: '#FFD9B0' }}>.</span>
      </div>
      <div style={{ fontFamily: F.body, fontSize: 13, fontWeight: 600, letterSpacing: 1, color: 'rgba(255,255,255,0.85)', marginTop: 6 }}>
        128 tick · ranked · pick your maps and queue →
      </div>
    </button>
  );
};

// ---------------------------------------------------------------------------
// PARTY VIEW — full player cards under FIND MATCH CTA
// ---------------------------------------------------------------------------

const PartyMemberMenu = ({ el, member, isYou, isYouLeader, isAlreadyFriend, onClose, onPromote, onKick, onAddFriend, onViewProfile }) => {
  const menuRef = React.useRef(null);
  const [pos, setPos] = React.useState({ left: -9999, top: -9999, ready: false });

  // Always place the menu directly below the card, centred on it.
  // No flip logic — party cards are always near the top of the page.
  React.useLayoutEffect(() => {
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const MENU_WIDTH = 200;
    const GAP = 6;
    const left = Math.max(
      8,
      Math.min(window.innerWidth - MENU_WIDTH - 8, rect.left + rect.width / 2 - MENU_WIDTH / 2)
    );
    const top = rect.bottom + GAP;
    setPos({ left, top, ready: true });
  }, [el]);

  // Close on outside click
  React.useEffect(() => {
    const h = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [onClose]);

  // Close on ESC
  React.useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);
 
  // Build items list
  const items = [];
  items.push({ label: 'View Profile', icon: '◆', action: onViewProfile });
 
  if (!isYou && !isAlreadyFriend) {
    items.push({ label: 'Add Friend', icon: '+', action: onAddFriend });
  }
 
  if (isYouLeader && !isYou) {
    items.push({ label: 'Promote to Leader', icon: '★', action: onPromote, accent: 'orange' });
    items.push({ label: 'Kick from Party', icon: '×', action: onKick, accent: 'red' });
  }
 
  return (
    <div
      ref={menuRef}
      style={{
        position: 'fixed',
        left: pos.left,
        top: pos.top,
        opacity: pos.ready ? 1 : 0,
        width: 200,
        background: C.card,
        borderTop:    `1px solid ${C.borderBright}`,
        borderRight:  `1px solid ${C.borderBright}`,
        borderBottom: `1px solid ${C.borderBright}`,
        borderLeft:   `2px solid ${C.red}`,
        boxShadow: `0 12px 32px rgba(0,0,0,0.65)`,
        zIndex: 250,
        overflow: 'hidden',
      }}
    >
      {/* Header — name of the player whose menu this is */}
      <div style={{
        padding: '10px 14px',
        borderTop: 'none', borderRight: 'none', borderLeft: 'none',
        borderBottom: `1px solid ${C.border}`,
      }}>
        <div style={{ fontFamily: F.body, fontSize: 9, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: C.textDim }}>
          Player
        </div>
        <div style={{
          fontFamily: F.display, fontSize: 16, letterSpacing: 1.5,
          color: C.text, textTransform: 'uppercase', lineHeight: 1, marginTop: 2,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {member.username}
        </div>
      </div>
 
      {/* Items */}
      {items.map((item, i) => (
        <PartyMenuItem key={i} {...item} onAfter={onClose} />
      ))}
    </div>
  );
};
 
const PartyMenuItem = ({ label, icon, action, accent, onAfter }) => {
  const [hover, hp] = useHover();
  const accentColour = accent === 'red' ? C.red : accent === 'orange' ? C.orange : null;
  const textColour   = accentColour
    ? (hover ? accentColour : `${accentColour}cc`)
    : (hover ? C.text : C.textDim);
  const bgHover = accent === 'red'    ? 'rgba(232,25,44,0.10)'
                : accent === 'orange' ? 'rgba(255,107,43,0.10)'
                : 'rgba(255,255,255,0.04)';
 
  return (
    <button
      {...hp}
      onClick={async () => {
        try { await action?.(); } catch (_) {}
        onAfter?.();
      }}
      style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 10,
        padding: '10px 14px',
        background: hover ? bgHover : 'transparent',
        borderTop: 'none', borderRight: 'none', borderBottom: 'none', borderLeft: 'none',
        cursor: 'pointer', textAlign: 'left',
        color: textColour,
        fontFamily: F.body, fontSize: 13, fontWeight: 600, letterSpacing: 0.5,
        transition: 'background 160ms ease, color 160ms ease',
      }}
    >
      <span style={{
        width: 16, display: 'inline-flex', justifyContent: 'center',
        fontFamily: F.display, fontSize: 14, color: accentColour || C.textDim,
      }}>
        {icon}
      </span>
      {label}
    </button>
  );
};

// Single player card — leader, member, or empty slot
const PartyMemberCard = ({ member, isYou, isLeader, onInvite, onCardClick, mode }) => {
  const [hover, hp] = useHover();
  const cardRef = React.useRef(null);
  const empty = !member;

  if (empty) {
    return (
      <button {...hp} onClick={onInvite} style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        gap: 8, padding: '18px 10px', flex: '1 1 0', minWidth: 0,
        background: hover ? C.cardAlt : 'transparent',
        borderTop:    `1px dashed ${hover ? C.borderBright : C.border}`,
        borderRight:  `1px dashed ${hover ? C.borderBright : C.border}`,
        borderBottom: `2px dashed ${hover ? C.orange : C.border}`,
        borderLeft:   `1px dashed ${hover ? C.borderBright : C.border}`,
        cursor: 'pointer', transition: 'background 160ms ease, border-color 160ms ease',
      }}>
        <div style={{
          width: 56, height: 56,
          borderTop: `1px dashed ${C.borderBright}`, borderRight: `1px dashed ${C.borderBright}`,
          borderBottom: `1px dashed ${C.borderBright}`, borderLeft: `1px dashed ${C.borderBright}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <span style={{ fontFamily: F.display, fontSize: 28, color: C.textVeryDim, lineHeight: 1 }}>+</span>
        </div>
        <div style={{
          fontFamily: F.display, fontSize: 13, letterSpacing: 1.5,
          color: hover ? C.text : C.textVeryDim, textTransform: 'uppercase',
          textAlign: 'center', lineHeight: 1.1,
        }}>
          Empty
        </div>
        <div style={{
          fontFamily: F.body, fontSize: 9, fontWeight: 700, letterSpacing: 1.5,
          color: C.textVeryDim, textTransform: 'uppercase', textAlign: 'center',
        }}>
          Invite
        </div>
      </button>
    );
  }

  const rankInfo = member.rank || null;
  const elo      = rankInfo ? publicRankElo(rankInfo, mode) : (member.elo ?? member.current_elo ?? null);
  const rankName = rankInfo ? publicRankName(rankInfo, mode) : (eloToRank(elo)?.name || 'Unranked');
  const placement = rankInfo ? rankPlacementLabel(rankInfo, mode) : null;
  const username = member.username || 'Player';
  const initial  = username.slice(0, 1).toUpperCase();
  const accent   = isYou ? C.red : isLeader ? C.orange : C.borderBright;

  const handleClick = () => {
    if (!cardRef.current) return;
    onCardClick?.(member, cardRef.current);
  };

  return (
    <button
      {...hp}
      ref={cardRef}
      onClick={handleClick}
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        gap: 8, padding: '14px 10px', flex: '1 1 0', minWidth: 0,
        background: hover ? C.cardAlt : C.card,
        borderTop:    `1px solid ${hover ? C.borderBright : C.border}`,
        borderRight:  `1px solid ${hover ? C.borderBright : C.border}`,
        borderBottom: `2px solid ${accent}`,
        borderLeft:   `1px solid ${hover ? C.borderBright : C.border}`,
        position: 'relative',
        cursor: 'pointer',
        fontFamily: 'inherit',
        transition: 'background 160ms ease, border-color 160ms ease',
      }}>
      {(isYou || isLeader) && (
        <div style={{
          position: 'absolute', top: -1, left: -1,
          fontFamily: F.body, fontSize: 8, fontWeight: 700, letterSpacing: 2,
          textTransform: 'uppercase',
          color: (isYou && isLeader) ? C.orange : isYou ? C.red : C.orange,
          background: C.card,
          padding: '3px 7px',
          borderTop:    `1px solid ${accent}`,
          borderRight:  `1px solid ${accent}`,
          borderBottom: `1px solid ${accent}`,
          borderLeft:   `1px solid ${accent}`,
        }}>
          {isYou && isLeader ? 'Leader · You' : isYou ? 'You' : 'Leader'}
        </div>
      )}

      <div style={{
        width: 56, height: 56, background: C.cardAlt,
        borderTop: `1px solid ${C.border}`, borderRight: `1px solid ${C.border}`,
        borderBottom: `1px solid ${C.border}`, borderLeft: `1px solid ${C.border}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
      }}>
        {member.avatar_url
          ? <img src={member.avatar_url} alt={username} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          : <span style={{ fontFamily: F.display, fontSize: 22, color: C.text }}>{initial}</span>
        }
      </div>

      <div style={{
        fontFamily: F.display, fontSize: 14, letterSpacing: 1,
        color: C.text, textTransform: 'uppercase', textAlign: 'center',
        lineHeight: 1.1, maxWidth: '100%',
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      }}>
        {username}
      </div>

      <RankBadge elo={elo} rankInfo={rankInfo} mode={mode} size={32} />

      <div style={{ textAlign: 'center', marginTop: 2 }}>
        <div style={{ fontFamily: F.display, fontSize: 20, color: C.text, letterSpacing: 0.5, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
          {elo ?? '—'}
        </div>
        <div style={{ fontFamily: F.body, fontSize: 8, fontWeight: 700, letterSpacing: 2, color: C.textDim, textTransform: 'uppercase', marginTop: 3 }}>
          {placement || 'Elo'}
        </div>
      </div>

      <div style={{
        fontFamily: F.body, fontSize: 9, fontWeight: 700, letterSpacing: 1.5,
        color: C.textDim, textTransform: 'uppercase', textAlign: 'center',
        lineHeight: 1.2, maxWidth: '100%',
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      }}>
        {rankName}
      </div>
    </button>
  );
};

const PartyView = ({ user, userRank, onOpenQueue, currentParty, friends, reloadParty, reloadFriends }) => {
  const navigate = useNavigate(); // already imported at top of file
  const { queueActive, preferredMode, setPreferredMode } = useQueue();

  // Mode: read from party in database (syncs across players + survives refresh).
  // Falls back to preferredMode only when solo (no party).
  const mode = currentParty?.mode || preferredMode;

  // Keep local preferredMode in sync when party mode arrives from backend
  useEffect(() => {
    if (currentParty?.mode) setPreferredMode(currentParty.mode);
  }, [currentParty?.mode, setPreferredMode]);

  const [modeChanging, setModeChanging] = useState(false);
  const [modeError, setModeError] = useState(null);
  useEffect(() => {
    if (!modeError) return;
    const t = setTimeout(() => setModeError(null), 3500);
    return () => clearTimeout(t);
  }, [modeError]);

  const handleModeChange = async (newMode) => {
    if (!youAreLeader) return; // only leader can change mode
    if (queueActive || modeChanging) return;
    const memberCount = Math.max(1, currentParty?.members?.length || 1);
    if (newMode === MODE_WINGMAN && memberCount > 2) {
      setModeError(`Too many players for Wingman (${memberCount}/2). Kick members first.`);
      return;
    }
    setModeChanging(true);
    try {
      if (currentParty) {
        await setPartyMode(newMode);
        reloadParty?.();
      }
      setPreferredMode(newMode);
      setModeError(null);
    } catch (e) {
      setModeError(e?.response?.data?.detail || 'Failed to change mode');
    } finally {
      setModeChanging(false);
    }
  };

  // Menu state — which member is the menu open for, and what's its anchor rect
  const [menuFor, setMenuFor] = useState(null);

  const maxSlots = mode === MODE_WINGMAN ? 2 : 5;
 
  const selfMember = {
    user_id: user?.id || user?.user_id,
    username: user?.username,
    avatar_url: user?.avatar_url,
    elo: publicRankElo(userRank, mode),
    rank: rankForMode(userRank, mode),
  };
 
  const others = (currentParty?.members || []).filter(m =>
    (m.user_id || m.id) !== (user?.id || user?.user_id)
  );
 
  const leaderId   = currentParty?.leader_id;
  const youAreLeader = leaderId
    ? leaderId === selfMember.user_id
    : true; // solo → you are leader by default
 
  const slots = [];
  slots.push({
    member: selfMember,
    isYou: true,
    isLeader: youAreLeader,
  });
  for (let i = 0; i < maxSlots - 1; i++) {
    const m = others[i];
    slots.push({
      member: m,
      isYou: false,
      isLeader: m && leaderId === (m.user_id || m.id),
    });
  }
 
  const filledMembers = slots.map(s => s.member).filter(Boolean);
  const elosWithValues = filledMembers.map(m => m.rank ? publicRankElo(m.rank, mode) : m.elo).filter(n => typeof n === 'number');
  const avgElo = elosWithValues.length > 0
    ? Math.round(elosWithValues.reduce((a, b) => a + b, 0) / elosWithValues.length)
    : null;
  const avgRank = avgElo != null ? eloToRank(avgElo) : null;
 
  // Is the menu-target already a friend?
  const isAlreadyFriend = (member) => {
    if (!friends || !member) return false;
    return friends.some(f =>
      f.username === member.username ||
      f.id === member.user_id ||
      f.user_id === member.user_id ||
      f.friend_id === member.user_id
    );
  };
 
  // Card click handler — opens menu (but not for your own card, where it does nothing)
  const handleCardClick = (member, el) => {
    setMenuFor({ member, el });
  };
 
  // Menu action handlers
  const handleViewProfile = () => {
    if (menuFor?.member?.username) {
      navigate(`/profile/${menuFor.member.username}`);
    }
  };
 
  const handleAddFriend = async () => {
    if (!menuFor?.member?.username) return;
    try {
      await addFriend(menuFor.member.username);
      reloadFriends?.();
    } catch (_) {}
  };
 
  const handlePromote = async () => {
    const id = menuFor?.member?.user_id || menuFor?.member?.id;
    if (!id) return;
    try { await promotePartyLeader(id); } catch (e) {
      console.error('promotePartyLeader failed (endpoint may not exist yet):', e);
    }
    reloadParty?.();
  };
 
  const handleKick = async () => {
    const id = menuFor?.member?.user_id || menuFor?.member?.id;
    if (!id) return;
    try { await kickFromParty(id); } catch (_) {}
    reloadParty?.();
  };
 
  return (
    <div style={{
      background: C.card,
      borderTop: `1px solid ${C.border}`, borderRight: `1px solid ${C.border}`,
      borderBottom: `1px solid ${C.border}`, borderLeft: `2px solid ${C.orange}`,
      overflow: 'visible', // ← changed from hidden so menu can escape
    }}>
      {/* Header (unchanged from your current version — keep your Leave button + mode toggle here) */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '14px 16px',
        borderTop: 'none', borderRight: 'none', borderLeft: 'none',
        borderBottom: `1px solid ${C.border}`,
      }}>
        <Diamond size={7} color={C.orange} hollow={false} />
        <span style={{ fontFamily: F.display, fontSize: 17, letterSpacing: 2, color: C.text, textTransform: 'uppercase' }}>
          Party
        </span>
 
        {/* Leave Party button — keep as-is from your current file */}
        <button
          onClick={async () => {
            try { await leaveParty(); window.location.reload(); } catch (_) {}
          }}
          style={{
            background: 'transparent', border: `1px solid ${C.red}`, color: C.red,
            padding: '4px 10px', fontFamily: F.display, fontSize: 11, letterSpacing: 1.5,
            textTransform: 'uppercase', cursor: 'pointer', marginLeft: 'auto', marginRight: 8,
            transition: 'background 160ms ease',
          }}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(232, 25, 44, 0.1)'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
        >
          Leave Party
        </button>
 
        <div style={{
          display: 'flex', flexShrink: 0,
          borderTop:    `1px solid ${C.border}`,
          borderRight:  `1px solid ${C.border}`,
          borderBottom: `1px solid ${C.border}`,
          borderLeft:   `1px solid ${C.border}`,
        }}>
          {[
            { id: MODE_5V5,     label: '5v5' },
            { id: MODE_WINGMAN, label: 'Wingman' },
          ].map((m, i) => {
            const active = mode === m.id;
            return (
              <button
                key={m.id}
                onClick={() => handleModeChange(m.id)}
                disabled={queueActive || modeChanging || !youAreLeader}
                title={!youAreLeader ? 'Only the party leader can change mode' : undefined}
                style={{
                  background: active ? C.red : 'transparent',
                  color: active ? C.text : C.textDim,
                  borderTop: 'none', borderBottom: 'none',
                  borderLeft:  i === 0 ? 'none' : `1px solid ${C.border}`,
                  borderRight: 'none',
                  padding: '4px 12px',
                  fontFamily: F.display, fontSize: 12, letterSpacing: 1.5, textTransform: 'uppercase',
                  cursor: (queueActive || modeChanging || !youAreLeader) ? 'not-allowed' : 'pointer',
                  opacity: ((queueActive || modeChanging || !youAreLeader) && !active) ? 0.4 : 1,
                  transition: 'background 160ms ease, color 160ms ease',
                }}
              >
                {m.label}
              </button>
            );
          })}
        </div>
      </div>
 
      {/* Mode error — only visible to leader when validation fails */}
      {modeError && (
        <div style={{
          margin: '0 12px',
          padding: '8px 12px',
          background: 'rgba(232,25,44,0.08)',
          borderTop: `1px solid ${C.red}`, borderRight: `1px solid ${C.red}`,
          borderBottom: `1px solid ${C.red}`, borderLeft: `2px solid ${C.red}`,
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <Diamond size={6} color={C.red} hollow={false} />
          <span style={{ fontFamily: F.body, fontSize: 12, fontWeight: 600, color: C.text, letterSpacing: 0.5, lineHeight: 1.4 }}>
            {modeError}
          </span>
        </div>
      )}

      {/* Horizontal cards */}
      <div style={{ padding: 12, display: 'flex', gap: 8, alignItems: 'stretch' }}>
        {slots.map((slot, i) => (
          <PartyMemberCard
            key={i}
            member={slot.member}
            isYou={slot.isYou}
            isLeader={slot.isLeader}
            onInvite={onOpenQueue}
            onCardClick={(member, anchor) => {
              // Don't open menu for empty slots (handled by onInvite)
              // and don't open menu for your own card — nothing to do
              if (slot.isYou) return;
              handleCardClick(member, anchor);
            }}
            mode={mode}
          />
        ))}
      </div>
 
      {/* Footer — unchanged */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 14,
        padding: '14px 16px',
        background: C.cardAlt,
        borderTop: `1px solid ${C.border}`,
        borderRight: 'none', borderBottom: 'none', borderLeft: 'none',
      }}>
        <RankBadge elo={avgElo} rankInfo={avgElo == null ? { is_ranked: false } : null} size={32} />
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: F.body, fontSize: 9, fontWeight: 700, letterSpacing: 2.5, textTransform: 'uppercase', color: C.textDim }}>
            Party average
          </div>
          <div style={{ fontFamily: F.display, fontSize: 16, letterSpacing: 1.5, color: C.text, textTransform: 'uppercase', lineHeight: 1, marginTop: 3 }}>
            {avgRank?.name || 'Unranked'}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
          <span style={{ fontFamily: F.display, fontSize: 26, color: C.text, letterSpacing: 1, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
            {avgElo ?? '—'}
          </span>
          <span style={{ fontFamily: F.body, fontSize: 9, fontWeight: 700, letterSpacing: 2, color: C.textDim, textTransform: 'uppercase', marginTop: 3 }}>
            Avg Elo
          </span>
        </div>
      </div>
 
      {/* The menu itself — renders fixed-position so it can escape the card */}
      {menuFor && (
        <PartyMemberMenu
          el={menuFor.el}
          member={menuFor.member}
          isYou={false}
          isYouLeader={youAreLeader}
          isAlreadyFriend={isAlreadyFriend(menuFor.member)}
          onClose={() => setMenuFor(null)}
          onViewProfile={handleViewProfile}
          onAddFriend={handleAddFriend}
          onPromote={handlePromote}
          onKick={handleKick}
        />
      )}
    </div>
  );
};

// ===========================================================================
// FRIENDS WIDGET + OVERLAY
// ===========================================================================
const presenceColour = (status) =>
  status === 'in_match' ? C.red :
  status === 'in_queue' ? C.orange :
  status === 'offline'  ? C.textVeryDim :
  C.green;

// Status normaliser — treat anything that isn't an explicit offline string as "online"
// (the friends table doesn't track presence in detail per the project doc, so we
// default to a soft "online" rather than hiding friends entirely)
const normaliseStatus = (f) => {
  const s = (f.status || f.presence || '').toLowerCase();
  if (s === 'offline') return 'offline';
  if (s === 'in_match' || s === 'in-match' || s === 'inmatch') return 'in_match';
  if (s === 'in_queue' || s === 'in-queue' || s === 'inqueue') return 'in_queue';
  // Anything else (online, idle, away, '', undefined) → treat as online
  return 'online';
};

const AvatarTile = ({ friend, onClick }) => {
  const [hover, hp] = useHover();
  const initial = (friend.username || '?').slice(0, 1).toUpperCase();
  const status  = normaliseStatus(friend);
  const colour  = presenceColour(status);
  return (
    <button {...hp} onClick={() => onClick?.(friend)} title={friend.username} style={{
      position: 'relative', width: 44, height: 44, background: C.cardAlt,
      borderTop:    `1px solid ${hover ? C.borderBright : C.border}`,
      borderRight:  `1px solid ${hover ? C.borderBright : C.border}`,
      borderBottom: `1px solid ${hover ? C.borderBright : C.border}`,
      borderLeft:   `1px solid ${hover ? C.borderBright : C.border}`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      cursor: 'pointer', overflow: 'hidden', transition: 'border-color 160ms ease',
    }}>
      {friend.avatar_url
        ? <img src={friend.avatar_url} alt={friend.username} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        : <span style={{ fontFamily: F.display, fontSize: 18, color: C.text }}>{initial}</span>
      }
      <span style={{
        position: 'absolute', bottom: 3, right: 3, width: 8, height: 8, borderRadius: '50%',
        background: colour, boxShadow: `0 0 5px ${colour}88`,
        borderTop: `1.5px solid ${C.card}`, borderRight: `1.5px solid ${C.card}`,
        borderBottom: `1.5px solid ${C.card}`, borderLeft: `1.5px solid ${C.card}`,
      }} />
    </button>
  );
};

const FriendsOverlay = ({ open, onClose, friends, onInvite, pending, onAccept, onFriendsChanged, onPendingChanged }) => {
  const [search, setSearch]     = useState('');
  const [results, setResults]   = useState([]);
  const [addInput, setAddInput] = useState('');
  const [addStatus, setAddStatus] = useState(null);

  useEffect(() => {
    if (!open) { setSearch(''); setResults([]); setAddInput(''); setAddStatus(null); }
  }, [open]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = open ? 'hidden' : prev;
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [open, onClose]);

  useEffect(() => {
    if (!open || !search.trim()) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const res  = await searchUsers(search.trim());
        const data = res?.data || res;
        setResults(Array.isArray(data) ? data : data?.users || []);
      } catch (_) { setResults([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [search, open]);

  const handleAdd = async () => {
    if (!addInput.trim() || addStatus === 'sending') return;
    setAddStatus('sending');
    try {
      await addFriend(addInput.trim());
      setAddStatus('sent');
      onFriendsChanged?.();
      onPendingChanged?.();
      setTimeout(() => { setAddInput(''); setAddStatus(null); }, 2000);
    } catch (_) {
      setAddStatus('error');
      setTimeout(() => setAddStatus(null), 2000);
    }
  };

  if (!open) return null;

  const online  = (friends || []).filter(f => normaliseStatus(f) !== 'offline');
  const offline = (friends || []).filter(f => normaliseStatus(f) === 'offline');

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 100,
      background: 'rgba(12,9,5,0.85)',
      backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        width: '100%', maxWidth: 680, maxHeight: '88vh',
        background: C.card,
        borderTop:    `1px solid ${C.border}`,
        borderRight:  `1px solid ${C.border}`,
        borderBottom: `1px solid ${C.border}`,
        borderLeft:   `2px solid ${C.red}`,
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, padding: '18px 20px',
          borderTop: 'none', borderRight: 'none', borderLeft: 'none',
          borderBottom: `1px solid ${C.border}`,
        }}>
          <Diamond size={9} color={C.red} hollow={false} />
          <span style={{ fontFamily: F.display, fontSize: 24, letterSpacing: 2.5, color: C.text, textTransform: 'uppercase' }}>
            Friends<span style={{ color: C.red }}>.</span>
          </span>
          <span style={{ fontFamily: F.body, fontSize: 12, color: C.textDim, fontWeight: 600, letterSpacing: 1, marginLeft: 4 }}>
            {online.length} online · {(friends || []).length} total
          </span>
          <button onClick={onClose} style={{ marginLeft: 'auto', background: 'transparent', borderTop: 'none', borderRight: 'none', borderBottom: 'none', borderLeft: 'none', color: C.textDim, cursor: 'pointer', padding: 4, display: 'flex', transition: 'color 160ms ease' }}
            onMouseEnter={e => (e.currentTarget.style.color = C.text)}
            onMouseLeave={e => (e.currentTarget.style.color = C.textDim)}>
            <IconX size={20} />
          </button>
        </div>

        <div style={{ overflowY: 'auto', flex: 1 }}>
          {/* Add friend */}
          <div style={{ padding: '16px 20px', borderTop: 'none', borderRight: 'none', borderLeft: 'none', borderBottom: `1px solid ${C.border}` }}>
            <div style={{ fontFamily: F.body, fontSize: 10, fontWeight: 700, letterSpacing: 2.5, textTransform: 'uppercase', color: C.textDim, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Diamond size={6} color={C.red} hollow={false} /> Add Friend
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{
                flex: 1, display: 'flex', alignItems: 'center', gap: 8, background: C.cardAlt,
                borderTop:    `1px solid ${C.border}`, borderRight: `1px solid ${C.border}`,
                borderBottom: `1px solid ${C.border}`, borderLeft: `2px solid ${C.borderBright}`,
                padding: '8px 12px',
              }}>
                <IconSearch size={15} color={C.textDim} />
                <input value={addInput} onChange={e => setAddInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleAdd()} placeholder="Enter username…"
                  style={{ flex: 1, background: 'transparent', borderTop: 'none', borderRight: 'none', borderBottom: 'none', borderLeft: 'none', outline: 'none', color: C.text, fontFamily: F.body, fontSize: 14, fontWeight: 500 }} />
              </div>
              <button onClick={handleAdd} disabled={!addInput.trim() || addStatus === 'sending'} style={{
                padding: '0 18px', cursor: addInput.trim() ? 'pointer' : 'not-allowed',
                background: addStatus === 'sent' ? '#1a3a2a' : addStatus === 'error' ? '#3a1a1a' : C.red,
                color: C.text, fontFamily: F.display, fontSize: 13, letterSpacing: 2, textTransform: 'uppercase',
                borderTop: 'none', borderRight: 'none', borderBottom: 'none', borderLeft: 'none',
                opacity: !addInput.trim() ? 0.5 : 1, transition: 'background 200ms ease', whiteSpace: 'nowrap',
              }}>
                {addStatus === 'sending' ? '…' : addStatus === 'sent' ? '✓ Sent' : addStatus === 'error' ? 'Not found' : 'Add →'}
              </button>
            </div>
          </div>

          {/* Search friends */}
          <div style={{ padding: '14px 20px', borderTop: 'none', borderRight: 'none', borderLeft: 'none', borderBottom: `1px solid ${C.border}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: C.cardAlt, borderTop: `1px solid ${C.border}`, borderRight: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}`, borderLeft: `2px solid ${C.borderBright}`, padding: '8px 12px' }}>
              <IconSearch size={15} color={C.textDim} />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search all players…"
                style={{ flex: 1, background: 'transparent', borderTop: 'none', borderRight: 'none', borderBottom: 'none', borderLeft: 'none', outline: 'none', color: C.text, fontFamily: F.body, fontSize: 14, fontWeight: 500 }} />
            </div>
          </div>

          {search.trim() && results.length > 0 && (
            <OverlaySection label="Results">
              {results.map(u => <OverlayFriendRow key={u.id || u.username} friend={u} onInvite={onInvite} />)}
            </OverlaySection>
          )}

          {pending?.length > 0 && (
            <OverlaySection label={`Pending requests (${pending.length})`}>
              {pending.map(req => (
                <div key={req.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 20px', borderTop: 'none', borderRight: 'none', borderLeft: 'none', borderBottom: `1px solid ${C.border}` }}>
                  <Diamond size={8} color={C.orange} hollow={false} />
                  <span style={{ flex: 1, fontFamily: F.body, fontSize: 13, color: C.text, fontWeight: 600 }}>
                    {req.username || req.requester_username}
                  </span>
                  <button onClick={() => onAccept?.(req)} style={{ background: C.red, color: C.text, borderTop: 'none', borderRight: 'none', borderBottom: 'none', borderLeft: 'none', padding: '6px 14px', cursor: 'pointer', fontFamily: F.display, fontSize: 12, letterSpacing: 1.5, textTransform: 'uppercase' }}>
                    Accept
                  </button>
                </div>
              ))}
            </OverlaySection>
          )}

          {online.length > 0 && (
            <OverlaySection label={`Online (${online.length})`}>
              {online.map(f => <OverlayFriendRow key={f.id || f.username} friend={f} onInvite={onInvite} />)}
            </OverlaySection>
          )}

          {offline.length > 0 && (
            <OverlaySection label={`Offline (${offline.length})`}>
              {offline.map(f => <OverlayFriendRow key={f.id || f.username} friend={f} onInvite={onInvite} showInvite={false} />)}
            </OverlaySection>
          )}

          {(friends || []).length === 0 && !search.trim() && (
            <div style={{ padding: '24px 20px', fontFamily: F.body, fontSize: 13, color: C.textVeryDim, fontWeight: 500 }}>
              No friends yet. Search above to add players.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const OverlaySection = ({ label, children }) => (
  <section>
    <div style={{
      padding: '10px 20px 6px', fontFamily: F.body, fontSize: 10, fontWeight: 700,
      letterSpacing: 2.5, textTransform: 'uppercase', color: C.textDim,
      display: 'flex', alignItems: 'center', gap: 8,
    }}>
      <Diamond size={6} color={C.red} hollow={false} /> {label}
    </div>
    {children}
  </section>
);

const OverlayFriendRow = ({ friend, onInvite, showInvite = true }) => {
  const [hover, hp] = useHover();
  const [inviteStatus, setInviteStatus] = useState(null); // Add state for visual feedback
  const status   = normaliseStatus(friend);
  const colour   = presenceColour(status);
  const busy     = status === 'in_match' || status === 'in_queue';
  const initial  = (friend.username || '?').slice(0, 1).toUpperCase();

  // Handle the click and update the button text
  const handleInviteClick = async () => {
    if (busy || inviteStatus === 'sent') return;
    setInviteStatus('sending');
    try {
      await onInvite?.(friend);
      setInviteStatus('sent');
      // Reset the button back to "Inv" after 3 seconds
      setTimeout(() => setInviteStatus(null), 20000);
    } catch (error) {
      console.error("Failed to send invite", error);
      setInviteStatus(null); // Reset if it failed
    }
  };

  return (
    <div {...hp} style={{
      display: 'flex', alignItems: 'center', gap: 12, padding: '8px 20px',
      background: hover ? C.cardAlt : 'transparent',
      borderTop: 'none', borderRight: 'none', borderLeft: 'none',
      borderBottom: `1px solid ${C.border}`,
      transition: 'background 160ms ease',
    }}>
      <div style={{
        width: 32, height: 32, flexShrink: 0, background: C.cardAlt,
        borderTop: `1px solid ${C.border}`, borderRight: `1px solid ${C.border}`,
        borderBottom: `1px solid ${C.border}`, borderLeft: `2px solid ${C.borderBright}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
      }}>
        {friend.avatar_url
          ? <img src={friend.avatar_url} alt={friend.username} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          : <span style={{ fontFamily: F.display, fontSize: 14, color: C.text }}>{initial}</span>
        }
      </div>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: colour, boxShadow: `0 0 5px ${colour}66`, flexShrink: 0 }} />
      <span style={{ flex: 1, fontFamily: F.body, fontSize: 13, fontWeight: 600, color: C.text, letterSpacing: 0.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {friend.username || 'Unknown'}
      </span>
      {showInvite && (
        <button onClick={handleInviteClick} disabled={busy || inviteStatus === 'sent'} style={{
          background: 'transparent', 
          color: busy ? C.textVeryDim : inviteStatus === 'sent' ? C.green : C.text,
          borderTop:    `1px solid ${busy ? C.border : inviteStatus === 'sent' ? C.green : C.borderBright}`,
          borderRight:  `1px solid ${busy ? C.border : inviteStatus === 'sent' ? C.green : C.borderBright}`,
          borderBottom: `1px solid ${busy ? C.border : inviteStatus === 'sent' ? C.green : C.borderBright}`,
          borderLeft:   `1px solid ${busy ? C.border : inviteStatus === 'sent' ? C.green : C.borderBright}`,
          cursor: (busy || inviteStatus === 'sent') ? 'default' : 'pointer',
          padding: '4px 10px', fontFamily: F.display, fontSize: 11, letterSpacing: 1.5, textTransform: 'uppercase',
          opacity: (busy || inviteStatus === 'sent') ? 0.6 : 0.8, transition: 'all 160ms ease',
        }}
          onMouseEnter={e => { if (!busy && inviteStatus !== 'sent') e.currentTarget.style.opacity = '1'; }}
          onMouseLeave={e => { e.currentTarget.style.opacity = (busy || inviteStatus === 'sent') ? '0.6' : '0.8'; }}>
          
          {/* Change button text based on status */}
          {inviteStatus === 'sending' ? '...' : inviteStatus === 'sent' ? 'Sent' : 'Inv'}
          
        </button>
      )}
    </div>
  );
};

// Compact widget — falls back to offline friends when none online
const FriendsWidget = ({ friends, loading, onExpand, onInvite }) => {
  const all     = friends || [];
  const online  = all.filter(f => normaliseStatus(f) !== 'offline');
  const offline = all.filter(f => normaliseStatus(f) === 'offline');

  // Fall back to offline if no one online — better than showing "nobody online"
  // when the user can see we have friends
  const showOffline = online.length === 0 && offline.length > 0;
  const visibleList = showOffline ? offline : online;
  const visible     = visibleList.slice(0, 8);

  return (
    <div style={{
      background: C.card,
      borderTop: `1px solid ${C.border}`, borderRight: `1px solid ${C.border}`,
      borderBottom: `1px solid ${C.border}`, borderLeft: `2px solid ${C.green}`,
      display: 'flex', flexDirection: 'column', overflow: 'hidden',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px',
        borderTop: 'none', borderRight: 'none', borderLeft: 'none',
        borderBottom: `1px solid ${C.border}`,
      }}>
        <Diamond size={7} color={C.green} hollow={false} />
        <span style={{ fontFamily: F.display, fontSize: 16, letterSpacing: 2, color: C.text, textTransform: 'uppercase', flex: 1 }}>
          Friends
        </span>
        <span style={{ fontFamily: F.body, fontSize: 11, fontWeight: 700, color: C.textDim, letterSpacing: 1.5 }}>
          {loading ? '…' : `${online.length} online · ${all.length} total`}
        </span>
      </div>

      <div style={{ padding: 14 }}>
        {loading ? (
          <div style={{ fontFamily: F.body, fontSize: 12, color: C.textVeryDim, fontWeight: 600, letterSpacing: 1, padding: '8px 0' }}>Loading…</div>
        ) : all.length === 0 ? (
          <div style={{ fontFamily: F.body, fontSize: 12, color: C.textVeryDim, fontWeight: 600, letterSpacing: 1, lineHeight: 1.5, padding: '4px 0' }}>
            No friends yet. Click Add Friend below.
          </div>
        ) : (
          <>
            {showOffline && (
              <div style={{ fontFamily: F.body, fontSize: 10, fontWeight: 700, letterSpacing: 2, color: C.textVeryDim, textTransform: 'uppercase', marginBottom: 8 }}>
                Nobody online · showing offline
              </div>
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {visible.map(f => (
                <AvatarTile key={f.id || f.username} friend={f} onClick={onInvite} />
              ))}
              {visibleList.length > 8 && (
                <button onClick={onExpand} style={{
                  width: 44, height: 44, background: C.cardAlt, cursor: 'pointer',
                  borderTop: `1px solid ${C.border}`, borderRight: `1px solid ${C.border}`,
                  borderBottom: `1px solid ${C.border}`, borderLeft: `1px solid ${C.border}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: F.body, fontSize: 11, fontWeight: 700, color: C.textDim, letterSpacing: 0.5,
                }}>
                  +{visibleList.length - 8}
                </button>
              )}
            </div>
          </>
        )}
      </div>

      <div style={{
        display: 'grid', gridTemplateColumns: '1fr 1fr',
        borderTop: `1px solid ${C.border}`,
        borderRight: 'none', borderBottom: 'none', borderLeft: 'none',
      }}>
        <WidgetFooterBtn onClick={onExpand} label="View all →" />
        <WidgetFooterBtn onClick={onExpand} label="+ Add friend" accent />
      </div>
    </div>
  );
};

const WidgetFooterBtn = ({ label, onClick, accent }) => {
  const [hover, hp] = useHover();
  return (
    <button {...hp} onClick={onClick} style={{
      background: hover ? (accent ? `rgba(232,25,44,0.06)` : 'rgba(255,255,255,0.02)') : 'transparent',
      borderTop: 'none', borderBottom: 'none',
      borderLeft:  accent ? `1px solid ${C.border}` : 'none',
      borderRight: 'none',
      padding: '12px 14px', cursor: 'pointer',
      fontFamily: F.display, fontSize: 12, letterSpacing: 1.5, textTransform: 'uppercase',
      color: accent ? (hover ? C.red : C.textDim) : (hover ? C.text : C.textDim),
      transition: 'background 160ms ease, color 160ms ease', textAlign: 'center',
    }}>
      {label}
    </button>
  );
};

// ===========================================================================
// HEADER — fills the top-right with a live status panel
// ===========================================================================
const HeaderStatusPanel = ({ rank, mode }) => {
  const elo = publicRankElo(rank, mode);
  const rankName = publicRankName(rank, mode);
  const placement = rankPlacementLabel(rank, mode);
  const [tick, setTick] = useState(true);
  useEffect(() => {
    const i = setInterval(() => setTick(t => !t), 900);
    return () => clearInterval(i);
  }, []);

  return (
    <div style={{
      display: 'flex', alignItems: 'stretch', gap: 0,
      background: C.card,
      borderTop:    `1px solid ${C.border}`,
      borderRight:  `1px solid ${C.border}`,
      borderBottom: `1px solid ${C.border}`,
      borderLeft:   `2px solid ${C.red}`,
      overflow: 'hidden',
    }}>
      {/* Servers online */}
      <div style={{ padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{
          width: 8, height: 8, borderRadius: '50%', background: C.green,
          boxShadow: tick ? `0 0 0 0 ${C.green}55, 0 0 10px ${C.green}` : `0 0 0 4px ${C.green}00, 0 0 4px ${C.green}66`,
          transition: 'box-shadow 900ms ease-out',
        }} />
        <div>
          <div style={{ fontFamily: F.body, fontSize: 9, fontWeight: 700, letterSpacing: 2, color: C.textDim, textTransform: 'uppercase' }}>
            Servers
          </div>
          <div style={{ fontFamily: F.display, fontSize: 15, letterSpacing: 1.5, color: C.text, textTransform: 'uppercase', lineHeight: 1, marginTop: 2 }}>
            All online
          </div>
        </div>
      </div>

      {/* Divider */}
      <div style={{ width: 1, background: C.border, alignSelf: 'stretch' }} />

      {/* Current ELO with rank */}
      <div style={{ padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
        <RankBadge elo={elo} rankInfo={rank} mode={mode} size={32} />
        <div>
          <div style={{ fontFamily: F.body, fontSize: 9, fontWeight: 700, letterSpacing: 2, color: C.textDim, textTransform: 'uppercase' }}>
            {rankName}
          </div>
          <div style={{ fontFamily: F.display, fontSize: 20, letterSpacing: 1, color: C.text, lineHeight: 1, marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>
            {elo ?? '—'} <span style={{ fontSize: 11, color: C.textDim, letterSpacing: 2 }}>{placement || 'ELO'}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const DashboardHeader = ({ username, queueActive, rank, isSmall, mode }) => (
  <div style={{
    display: 'flex', alignItems: isSmall ? 'flex-start' : 'flex-end', gap: 24,
    flexDirection: isSmall ? 'column' : 'row',
    paddingBottom: 24,
    borderTop: 'none', borderRight: 'none', borderLeft: 'none',
    borderBottom: `1px solid ${C.border}`,
    marginBottom: 24,
  }}>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        fontFamily: F.body, fontSize: 10, fontWeight: 700,
        letterSpacing: 2.5, textTransform: 'uppercase', color: C.textDim,
      }}>
        <Diamond size={6} color={C.red} hollow={false} />
        <span>{queueActive ? 'In queue · cockpit' : 'Welcome back'}</span>
      </div>
      <h1 style={{
        fontFamily: F.display, fontSize: 56, letterSpacing: 1.5,
        lineHeight: 0.95, color: C.text, margin: '8px 0 0 0', textTransform: 'uppercase',
      }}>
        Ready{username ? `, ${username}` : ''}<span style={{ color: C.red }}>.</span>
      </h1>
    </div>
    {/* Status panel — fills the top-right empty space */}
    {!isSmall && <HeaderStatusPanel rank={rank} mode={mode} />}
  </div>
);

// ===========================================================================
// MAIN DASHBOARD
// ===========================================================================
export default function Dashboard() {
  const navigate  = useNavigate();
  const { user }  = useAuth();
  const isMobile  = useIsMobile(1180);
  const isSmall   = useIsMobile(720);
  const {
    queueActive,
    openQueueOverlay,
    preferredMode,
    party: currentParty,
    reloadParty,
    partyInvites,
    dismissPartyInvite,
    reloadNotifications,
  } = useQueue();

  // Data
  const [rank, setRank]             = useState(null);
  const [eloHistory, setEloHistory] = useState([]);
  const [eloDelta, setEloDelta]     = useState(null);
  const [matches, setMatches]       = useState([]);
  const [stats, setStats]           = useState({});

  // Friends
  const [friends, setFriends]       = useState([]);
  const [friendsLoading, setFriendsLoading] = useState(false);
  const [pending, setPending]       = useState([]);
  const [overlayOpen, setOverlayOpen] = useState(false);
  
  // --- Invite Popup State ---
  const [partyInvitePopup, setPartyInvitePopup] = useState(null);
  const [inviteCountdown, setInviteCountdown] = useState(20);
  const seenInviteIds = useRef(new Set());

  // Countdown timer for received invite
  useEffect(() => {
    if (!partyInvitePopup) return;
    setInviteCountdown(20);
    const interval = setInterval(() => {
      setInviteCountdown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          setPartyInvitePopup(null);
          return 20;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [partyInvitePopup]);

  useEffect(() => {
    const nextInvite = (partyInvites || []).find(n =>
      !n.read && !seenInviteIds.current.has(n.id)
    );
    if (!nextInvite) return;
    seenInviteIds.current.add(nextInvite.id);
    setPartyInvitePopup(nextInvite);
  }, [partyInvites]);

  // -----------------------------------------------------------------------
  // Loaders
  // -----------------------------------------------------------------------
  const loadRank = useCallback(async () => {
    if (!user?.username) return;
    try { const res = await getPlayerRank(user.username); setRank(res?.data || res); } catch (_) {}
  }, [user]);

  const loadElo = useCallback(async () => {
    try {
      const res  = await getEloHistory();
      const data = res?.data || res;
      const pts  = Array.isArray(data) ? data : data?.history || [];
      const elos = pts.map(p => typeof p === 'number' ? p : p.elo).filter(n => typeof n === 'number');
      setEloHistory(elos);
      if (elos.length >= 2) setEloDelta(elos[elos.length - 1] - elos[elos.length - 2]);
    } catch (_) {}
  }, []);

  const loadMatches = useCallback(async () => {
    if (!user?.username) return;
    try {
      const res    = await getMatchHistory(user.username);
      const data   = res?.data || res;
      const list   = Array.isArray(data) ? data : data?.matches || [];
      setMatches(list.slice(0, 5));
      if (list.length > 0) {
        const wins  = list.filter(m => m.result === 'win' || m.result === 'W' || (m.team_score != null && m.opponent_score != null && m.team_score > m.opponent_score)).length;
        const kills = list.map(m => m.kills ?? m.k).filter(n => typeof n === 'number');
        const deaths= list.map(m => m.deaths ?? m.d).filter(n => typeof n === 'number');
        const hsArr = list.map(m => m.hs_percent ?? m.headshot_percent).filter(n => typeof n === 'number');
        setStats({
          kd:      deaths.length > 0 ? kills.reduce((a,b)=>a+b,0) / deaths.reduce((a,b)=>a+b,0) : null,
          winRate: list.length > 0 ? (wins / list.length) * 100 : null,
          hs:      hsArr.length > 0 ? hsArr.reduce((a,b)=>a+b,0) / hsArr.length : null,
          matches: list.length,
        });
      }
    } catch (_) {}
  }, [user]);

  const loadFriends = useCallback(async () => {
    setFriendsLoading(true);
    try {
      const res  = await getFriends();
      const data = res?.data || res;
      const rawFriends = Array.isArray(data) ? data : data?.friends || [];

      // Flatten the nested 'users' object so the UI can read it normally
      const flatFriends = rawFriends.map(f => {
        const nestedUser = f.users || {};
        return {
          ...f,
          username: nestedUser.username || f.username,
          avatar_url: nestedUser.avatar_url || f.avatar_url,
          status: nestedUser.status || f.status,
          presence: nestedUser.presence || f.presence
        };
      });

      setFriends(flatFriends);
    } catch (_) { setFriends([]); } finally { setFriendsLoading(false); }
  }, []);

  const loadPending = useCallback(async () => {
    try {
      const res  = await getFriendRequests();
      const data = res?.data || res;
      setPending(Array.isArray(data) ? data : data?.requests || []);
    } catch (_) { setPending([]); }
  }, []);

  useEffect(() => {
    loadRank(); loadElo(); loadMatches(); loadFriends(); loadPending();
  }, [loadRank, loadElo, loadMatches, loadFriends, loadPending]);

  useEffect(() => {
    if (!overlayOpen) return;
    loadFriends();
    loadPending();
  }, [overlayOpen, loadFriends, loadPending]);

  useEffect(() => {
    const openFriends = () => {
      setOverlayOpen(true);
      loadFriends();
      loadPending();
    };
    window.addEventListener('repeaked:open-friends', openFriends);
    return () => window.removeEventListener('repeaked:open-friends', openFriends);
  }, [loadFriends, loadPending]);

  const handleInvite = useCallback(async (friend) => {
    try { await sendPartyInvite(friend.username); } catch (_) {}
  }, []);

  const handleAccept = useCallback(async (req) => {
    try {
      await acceptFriend(req.requester_id || req.user_id);
      setPending(prev => prev.filter(r => r.id !== req.id));
      loadFriends();
    } catch (_) {}
  }, [loadFriends]);

  const gridCols = isSmall ? '1fr' : isMobile ? '1fr 1fr' : '320px 1fr 300px';
  const displayMode = currentParty?.mode || preferredMode || MODE_5V5;

  return (
    <div style={{ padding: isSmall ? '24px 18px 32px' : isMobile ? '28px 24px 32px' : '32px 32px 40px', maxWidth: 1400, margin: '0 auto', animation: 'rp-page-up 420ms cubic-bezier(0.2, 0.7, 0.2, 1) both' }}>
      <style>{`
        @keyframes rp-page-up{from{opacity:0;transform:translateY(18px)}to{opacity:1;transform:translateY(0)}}
        @media (prefers-reduced-motion: reduce){*{animation-duration:1ms!important;transition-duration:1ms!important}}
      `}</style>
      <DashboardHeader username={user?.username} queueActive={queueActive} rank={rank} isSmall={isSmall} mode={displayMode} />

      <div style={{ display: 'grid', gridTemplateColumns: gridCols, gap: 20, alignItems: 'start' }}>
        {/* LEFT COLUMN — Rank + Sparkline (no stats here anymore) */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, gridColumn: isMobile && !isSmall ? '1 / -1' : 'auto' }}>
          <RankCard rank={rank} eloDelta={eloDelta} mode={displayMode} />
          <SparklineCard points={eloHistory} />
        </div>

        {/* CENTRE COLUMN — Recent Matches + Find Match + Party View */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <RecentMatches matches={matches} onOpenMatch={m => { const id = m.id || m.match_id; if (id) navigate(`/matches/${id}`); }} />
          <FindMatchCTA />
          <PartyView
            user={user}
            userRank={rank}
            onOpenQueue={openQueueOverlay}
            currentParty={currentParty}
            friends={friends}
            reloadParty={reloadParty}
            reloadFriends={loadFriends}
          />
        </div>

        {/* RIGHT COLUMN — Friends + Career Stats (swapped from drop widget) */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, gridColumn: isMobile && !isSmall ? '1 / -1' : 'auto' }}>
          <FriendsWidget
            friends={friends}
            loading={friendsLoading}
            onExpand={() => setOverlayOpen(true)}
            onInvite={handleInvite}
          />
          <QuickStatsCard stats={stats} />
        </div>
      </div>

      {/* --- INCOMING PARTY INVITE POPUP --- */}
      {partyInvitePopup && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 300,
          background: 'rgba(12,9,5,0.85)',
          backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
        }}>
          <div style={{
            width: '100%', maxWidth: 420,
            background: C.card,
            borderTop: `1px solid ${C.border}`, borderRight: `1px solid ${C.border}`,
            borderBottom: `1px solid ${C.border}`, borderLeft: `2px solid ${C.red}`,
            padding: 32, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16,
            textAlign: 'center',
          }}>
            <Eyebrow>Party Invite</Eyebrow>
            
            {(partyInvitePopup.data?.from_avatar || partyInvitePopup.from_avatar) ? (
              <img src={partyInvitePopup.data?.from_avatar || partyInvitePopup.from_avatar} alt="" style={{ width: 64, height: 64, border: `1px solid ${C.borderBright}`, objectFit: 'cover' }} />
            ) : (
              <Diamond size={48} color={C.red} withDot dotColor={C.orange} />
            )}

            <div style={{ fontFamily: F.display, fontSize: 28, letterSpacing: 1.5, color: C.text, textTransform: 'uppercase', lineHeight: 1.1 }}>
              <span style={{ color: C.red }}>{partyInvitePopup.data?.from_username || partyInvitePopup.from_username || 'A player'}</span> invited you
            </div>

            <div style={{ width: '100%', height: 4, background: C.cardAlt, marginTop: 8, position: 'relative' }}>
               <div style={{ 
                 position: 'absolute', top: 0, left: 0, height: '100%', 
                 width: `${(inviteCountdown / 20) * 100}%`, 
                 background: inviteCountdown > 7 ? C.red : C.orange, 
                 transition: 'width 1s linear, background 0.3s' 
               }} />
            </div>
            <div style={{ fontFamily: F.body, fontSize: 11, color: C.textDim, letterSpacing: 2, textTransform: 'uppercase', fontWeight: 600 }}>
              {inviteCountdown}s remaining
            </div>

            <div style={{ display: 'flex', gap: 12, width: '100%', marginTop: 8 }}>
              <button onClick={async () => {
                console.log("1. Accept clicked! Full notification payload:", partyInvitePopup);
                
                try {
                  const partyId = partyInvitePopup.data?.party_id || partyInvitePopup.party_id;
                  
                  if (partyId) {
                    // Try to leave any ghost party just in case
                    try { await leaveParty(); } catch(e) {}
                    
                    // Attempt the join
                    await joinParty(partyId);
                    
                    // IF SUCCESSFUL: Mark as read and refresh context-owned state.
                    await readNotification(partyInvitePopup.id);
                    dismissPartyInvite?.(partyInvitePopup.id);
                    await reloadParty?.();
                    await reloadNotifications?.();
                  } else {
                    await readNotification(partyInvitePopup.id);
                    dismissPartyInvite?.(partyInvitePopup.id);
                    setPartyInvitePopup(null);
                  }
                } catch (err) {
                  // IF IT FAILS: Do not reload. Show the error!
                  const errorMsg = err.response?.data?.error || err.response?.data?.message || "Unknown server error";
                  alert(`Cannot join party: ${errorMsg}`);
                  setPartyInvitePopup(null);
                }
                
                setPartyInvitePopup(null);
              }} style={{ 
                flex: 1, background: C.red, color: C.text, padding: '12px', 
                borderTop: 'none', borderRight: 'none', borderBottom: 'none', borderLeft: 'none',
                fontFamily: F.display, fontSize: 15, letterSpacing: 2, textTransform: 'uppercase', cursor: 'pointer' 
              }}>
                Accept
              </button>
              
              <button onClick={async () => {
                try { await readNotification(partyInvitePopup.id); } catch (_) {}
                dismissPartyInvite?.(partyInvitePopup.id);
                reloadNotifications?.();
                setPartyInvitePopup(null);
              }} style={{ 
                flex: 1, background: 'transparent', color: C.textDim, padding: '12px', 
                borderTop: `1px solid ${C.border}`, borderRight: `1px solid ${C.border}`, 
                borderBottom: `1px solid ${C.border}`, borderLeft: `1px solid ${C.border}`,
                fontFamily: F.display, fontSize: 15, letterSpacing: 2, textTransform: 'uppercase', cursor: 'pointer',
                transition: 'color 160ms ease, border-color 160ms ease'
              }}
              onMouseEnter={e => { e.currentTarget.style.color = C.text; e.currentTarget.style.borderColor = C.borderBright; }}
              onMouseLeave={e => { e.currentTarget.style.color = C.textDim; e.currentTarget.style.borderColor = C.border; }}>
                Decline
              </button>
            </div>
          </div>
        </div>
      )}

      <FriendsOverlay
        open={overlayOpen}
        onClose={() => setOverlayOpen(false)}
        friends={friends}
        onInvite={handleInvite}
        pending={pending}
        onAccept={handleAccept}
        onFriendsChanged={loadFriends}
        onPendingChanged={loadPending}
      />
    </div>
  );
}